/**
 * Hook para status de conexão com CorelDRAW
 * 
 * Monitora o status de conexão baseado em:
 * - Última resposta recebida do backend
 * - Ações longas pendentes
 * 
 * Conforme especificação em RESPOSTAS_BACKEND_DUVIDAS.md
 */

import { createContext, useContext, useState, useEffect, useMemo, ReactNode, useRef } from 'react';
import { LONG_RUNNING_ACTIONS, OFFLINE_THRESHOLD_MS } from '@/lib/corel/constants';
import { corelCommunication } from '@/lib/corel/communication';
import type { WebViewResponse } from '@/lib/corel/types/messages';
import { logger } from '@/lib/terminalLogger';
import { LogCategory } from '@/config/logging';

export interface HealthStatus {
  // Status principal
  status: 'connected' | 'disconnected' | 'busy';
  
  // Detalhes
  lastResponseAt: number | null;      // Timestamp da última resposta (qualquer)
  hasLongActionPending: boolean;      // Há ação longa pendente?
  pendingLongActions: string[];       // Quais ações longas estão pendentes
  
  // Conveniências
  isConnected: boolean;               // status === 'connected'
  isBusy: boolean;                    // status === 'busy'
  isDisconnected: boolean;            // status === 'disconnected'
}

const HealthStatusContext = createContext<HealthStatus | null>(null);

interface HealthStatusProviderProps {
  children: ReactNode;
}

/**
 * Provider para status de conexão com CorelDRAW
 * 
 * Monitora todas as respostas do backend e ações pendentes para determinar
 * o status de conexão (connected/disconnected/busy).
 */
export function HealthStatusProvider({ children }: HealthStatusProviderProps) {
  const [lastResponseAt, setLastResponseAt] = useState<number | null>(Date.now());
  const [pendingLongActions, setPendingLongActions] = useState<string[]>([]);
  
  // Refs para evitar múltiplos listeners e rastrear mudanças
  const listenerSetupRef = useRef(false);
  const previousStatusRef = useRef<'connected' | 'disconnected' | 'busy' | null>(null);
  const previousPendingActionsRef = useRef<string[]>([]);
  
  // Atualizar lastResponseAt quando receber qualquer resposta
  // ✅ SEGURO: setupListener agora cria wrapper que preserva callback anterior automaticamente
  useEffect(() => {
    // Evitar configurar múltiplos listeners
    if (listenerSetupRef.current) {
      return;
    }
    
    listenerSetupRef.current = true;
    
    // Registrar listener - setupListener agora cria wrapper que preserva callback anterior
    const cleanup = corelCommunication.setupListener((response: WebViewResponse) => {
      // Atualizar timestamp de última resposta (qualquer resposta prova conexão)
      const now = Date.now();
      setLastResponseAt(now);
      
      // Log de debug: resposta recebida (apenas nível DEBUG)
      logger.debug('[HEALTH_STATUS] Resposta recebida', {
        timestamp: now,
        requestId: (response as any)?.requestId,
        action: (response as any)?.action || 'unknown',
        status: (response as any)?.status
      });
    });
    
    return () => {
      listenerSetupRef.current = false;
      cleanup();
    };
  }, []);
  
  // Verificar ações longas pendentes periodicamente
  useEffect(() => {
    const updatePendingActions = () => {
      try {
        const pending = corelCommunication.getPendingRequestsInfo();
        const longActions = pending
          .filter(req => {
            const action = req.action || '';
            return LONG_RUNNING_ACTIONS.has(action);
          })
          .map(req => req.action || '')
          .filter(Boolean) as string[];
        
        // Log quando ações longas mudam (apenas nível DEBUG)
        const previous = previousPendingActionsRef.current;
        if (longActions.length !== previous.length || 
            longActions.some((a, i) => a !== previous[i])) {
          logger.debug('[HEALTH_STATUS] Ações longas atualizadas', {
            previous,
            current: longActions,
            count: longActions.length,
            added: longActions.filter(a => !previous.includes(a)),
            removed: previous.filter(a => !longActions.includes(a))
          });
          previousPendingActionsRef.current = longActions;
        }
        
        setPendingLongActions(longActions);
      } catch (error) {
        // Log de erro usando sistema centralizado
        logger.error('[HEALTH_STATUS] Erro ao atualizar ações pendentes', {
          error: error instanceof Error ? error.message : String(error)
        });
      }
    };
    
    // Não iniciar timer se não há token (tela de login)
    const hasToken = !!localStorage.getItem('cdr:auth:accessToken') || !!localStorage.getItem('accessToken');
    if (!hasToken) return;

    updatePendingActions();
    const interval = setInterval(updatePendingActions, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Calcular status baseado nas regras do backend
  const status = useMemo((): 'connected' | 'disconnected' | 'busy' => {
    const hasLongActionPending = pendingLongActions.length > 0;
    
    // REGRA 1: Se há ação longa pendente → SEMPRE busy
    // Não verificar conexão durante ações longas (assumir conectado)
    if (hasLongActionPending) {
      return 'busy';
    }
    
    // REGRA 2: Só verificar conexão quando NÃO há ação longa
    if (lastResponseAt === null) {
      return 'disconnected';
    }
    
    const timeSinceLastResponse = Date.now() - lastResponseAt;
    
    // Se recebeu resposta nos últimos 60s → connected
    if (timeSinceLastResponse < OFFLINE_THRESHOLD_MS) {
      return 'connected';
    }
    
    // Caso contrário → disconnected
    return 'disconnected';
  }, [lastResponseAt, pendingLongActions]);
  
  // Log quando status muda (apenas nível DEBUG)
  useEffect(() => {
    if (previousStatusRef.current !== null && previousStatusRef.current !== status) {
      const timeSinceLastResponse = lastResponseAt ? Date.now() - lastResponseAt : null;
      
      logger.debug('[HEALTH_STATUS] Status mudou', {
        from: previousStatusRef.current,
        to: status,
        lastResponseAt,
        timeSinceLastResponse,
        timeSinceLastResponseSeconds: timeSinceLastResponse ? Math.round(timeSinceLastResponse / 1000) : null,
        pendingLongActions,
        hasLongActionPending: pendingLongActions.length > 0,
        reason: pendingLongActions.length > 0 
          ? 'long_action_pending' 
          : (timeSinceLastResponse && timeSinceLastResponse >= OFFLINE_THRESHOLD_MS 
              ? 'no_response_threshold' 
              : 'recent_response')
      });
    }
    previousStatusRef.current = status;
  }, [status, lastResponseAt, pendingLongActions]);
  
  const value: HealthStatus = useMemo(() => ({
    status,
    lastResponseAt,
    hasLongActionPending: pendingLongActions.length > 0,
    pendingLongActions,
    isConnected: status === 'connected',
    isBusy: status === 'busy',
    isDisconnected: status === 'disconnected',
  }), [status, lastResponseAt, pendingLongActions]);
  
  return (
    <HealthStatusContext.Provider value={value}>
      {children}
    </HealthStatusContext.Provider>
  );
}

/**
 * Hook para acessar status de conexão com CorelDRAW
 * 
 * @returns HealthStatus com informações de conexão
 * @throws Error se usado fora do HealthStatusProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { status, isBusy, pendingLongActions } = useHealthStatus();
 *   
 *   if (isBusy) {
 *     return <div>Processando: {pendingLongActions.join(', ')}</div>;
 *   }
 *   
 *   return <div>Status: {status}</div>;
 * }
 * ```
 */
export function useHealthStatus(): HealthStatus {
  const context = useContext(HealthStatusContext);
  if (!context) {
    throw new Error('useHealthStatus must be used within HealthStatusProvider');
  }
  return context;
}

